import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { InfoTip } from "@/components/InfoTip";
import { Building2, Camera, Lock, CalendarClock, ArrowRight } from "lucide-react";

function urgency(days) {
  if (days == null) return { cls: "text-[#64748B]", label: "—" };
  if (days < 0) return { cls: "text-[#B91C1C] font-semibold", label: `${Math.abs(days)}d overdue` };
  if (days <= 7) return { cls: "text-[#B91C1C] font-semibold", label: `${days}d left` };
  if (days <= 21) return { cls: "text-[#92400E] font-semibold", label: `${days}d left` };
  return { cls: "text-[#166534]", label: `${days}d left` };
}

function Stat({ icon: Icon, label, value, testId, topic }) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-white/5 px-4 py-3" data-testid={testId}>
      <span className="grid place-items-center h-9 w-9 rounded-lg bg-amber/15 shrink-0"><Icon size={17} className="text-amber" /></span>
      <div className="min-w-0">
        <p className="text-xl font-bold text-white leading-none">{value}</p>
        <p className="text-[11px] text-white/60 mt-1 flex items-center gap-1">{label}{topic && <InfoTip topic={topic} dark side="top" />}</p>
      </div>
    </div>
  );
}

export function PortfolioStrip() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    api.get("/portfolio").then(({ data }) => { if (active) setData(data); }).catch((e) => console.error("portfolio load failed", e));
    return () => { active = false; };
  }, []);

  if (!data) return null;
  const real = data.properties.filter((p) => !p.is_sample && !p.is_demo);
  if (real.length < 2) return null;

  const totals = {
    properties: real.length,
    photos: real.reduce((s, p) => s + (p.photo_count || 0), 0),
    reports_locked: real.filter((p) => p.is_locked).length,
    deadlines_tracked: real.filter((p) => p.deadline?.configured).length,
  };

  const deadlines = real
    .filter((p) => p.deadline?.configured && p.deadline?.days_remaining != null)
    .sort((a, b) => a.deadline.days_remaining - b.deadline.days_remaining)
    .slice(0, 4);

  return (
    <div className="mb-6 rounded-2xl p-5 sm:p-6" style={{ background: "#0B1F3A" }} data-testid="portfolio-strip">
      <div className="flex items-center gap-2">
        <Building2 size={18} className="text-amber" />
        <h2 className="font-heading text-lg font-semibold text-white">Your portfolio</h2>
        <span className="text-xs text-white/50">· Plus</span>
      </div>

      <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={Building2} label="Properties" value={totals.properties} testId="portfolio-total-properties" topic="portfolioProperties" />
        <Stat icon={Camera} label="Photos documented" value={totals.photos} testId="portfolio-total-photos" />
        <Stat icon={Lock} label="Reports locked" value={totals.reports_locked} testId="portfolio-total-locked" />
        <Stat icon={CalendarClock} label="Deadlines tracked" value={totals.deadlines_tracked} testId="portfolio-total-deadlines" />
      </div>

      {deadlines.length > 0 && (
        <div className="mt-5">
          <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-white/50">
            <CalendarClock size={13} /> Upcoming deadlines
          </p>
          <ul className="mt-2 divide-y divide-white/10" data-testid="portfolio-deadlines">
            {deadlines.map((p) => {
              const u = urgency(p.deadline.days_remaining);
              return (
                <li key={p.property_id}>
                  <button onClick={() => navigate(`/property/${p.property_id}`)}
                    data-testid={`portfolio-deadline-${p.property_id}`}
                    className="w-full flex items-center justify-between gap-3 py-2.5 text-left group">
                    <span className="text-sm text-white/90 truncate group-hover:text-amber transition-colors">{p.address}</span>
                    <span className="flex items-center gap-2 shrink-0">
                      <span className={`text-xs ${u.cls} bg-white/10 rounded-full px-2 py-0.5`}>{u.label}</span>
                      <ArrowRight size={14} className="text-white/40" />
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}
